import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MobileLayout from '../MobileLayout';
import { apiGet } from '../api';
import { MobileDocument } from '../types';
import { jobsService } from '../../services/jobsService';
import { useBackgroundReports } from '../../contexts/BackgroundReportContext';
import { useToast } from '../../contexts/ToastContext';
import { apiPostJson } from '../api';

const PRIMARY = '#1993e5';

interface ProjectFull {
  id: string;
  title: string;
  customerId: string;
  customer?: { id: string; name: string; company?: string | null };
}

interface WorkflowChoice {
  type: string;
  label: string;
  description: string;
  minDocs: number;
}

// Curated mobile subset of the unified workflows. Each works on a small
// handful of docs and is useful in the field.
const WORKFLOWS: WorkflowChoice[] = [
  { type: 'loss_run_analysis', label: 'Loss Run Analysis', description: 'Summarize claims history from one or more loss runs.', minDocs: 1 },
  { type: 'renewal_analysis', label: 'Renewal Analysis', description: 'Review a renewal policy with market context.', minDocs: 1 },
  { type: 'coverage_analysis', label: 'Coverage Analysis', description: 'Pull key clauses, limits, and gaps from a policy.', minDocs: 1 },
  { type: 'research', label: 'Research', description: 'Market / company research — no document required.', minDocs: 0 },
];

// Mode IDs match the desktop WorkflowModal exactly so the backend prompt
// routing behaves identically.
const COVERAGE_MODES = [
  { id: 'Policy Clause Analysis', label: 'Clause analysis', description: 'Pull key clauses, limits, gaps from the policy text.' },
  { id: 'Policy Scenario Analysis', label: 'Scenario analysis', description: 'Evaluate how the policy would respond to a specific loss scenario.' },
  { id: 'General Review', label: 'General review', description: 'Comprehensive policy review (also needs a Client Risk Assessment doc).' },
] as const;

const MobileRunWorkflow: React.FC = () => {
  const { projectId = '' } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { addBackgroundReport, refreshReports } = useBackgroundReports();
  const { showToast } = useToast();

  const [project, setProject] = useState<ProjectFull | null>(null);
  const [documents, setDocuments] = useState<MobileDocument[]>([]);
  const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(new Set());
  const [chosenType, setChosenType] = useState<string>(WORKFLOWS[0].type);
  const [context, setContext] = useState('');
  const [coverageMode, setCoverageMode] = useState<string>('Policy Clause Analysis');
  const [scenarioText, setScenarioText] = useState('');
  // Research-specific fields. Backend requires URL or LinkedIn URL.
  const [researchUrl, setResearchUrl] = useState('');
  const [researchLinkedIn, setResearchLinkedIn] = useState('');
  const [researchNotes, setResearchNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const chosen = useMemo(() => WORKFLOWS.find((w) => w.type === chosenType)!, [chosenType]);
  const isCoverage = chosenType === 'coverage_analysis';
  const isScenarioMode = isCoverage && coverageMode === 'Policy Scenario Analysis';
  const isResearch = chosenType === 'research';

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const p = await apiGet<ProjectFull>(`/projects/${projectId}`);
        if (cancel) return;
        setProject({
          ...p,
          title: (p as any).title || (p as any).name || 'Project',
        });
        const docs = await apiGet<any[]>(`/customers/${p.customerId}/documents`);
        if (cancel) return;
        setDocuments((docs || [])
          .filter((d) => d.projectId === projectId)
          .map((d) => ({
            id: d.id, name: d.name, type: d.type, size: d.size, status: d.status,
            projectId: d.projectId, customerId: d.customerId, uploadedAt: d.uploadedAt,
          })));
      } catch (e) {
        if (!cancel) setError(e instanceof Error ? e.message : 'Failed to load project');
      }
    })();
    return () => { cancel = true; };
  }, [projectId]);

  const toggle = (id: string) => {
    setSelectedDocIds((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const docsOk = selectedDocIds.size >= chosen.minDocs;
  const scenarioOk = !isScenarioMode || scenarioText.trim().length > 0;
  const researchOk = !isResearch || researchUrl.trim().length > 0 || researchLinkedIn.trim().length > 0;
  const canRun = !!project && docsOk && scenarioOk && researchOk;

  const run = async () => {
    if (!project || !chosen) return;
    setSubmitting(true);
    setError(null);
    try {
      const docIds = Array.from(selectedDocIds);
      const token = localStorage.getItem('riskninja-token') || '';
      const customerName = project.customer?.company || project.customer?.name || 'Customer';

      let jobId: string | undefined;

      if (isResearch) {
        // Research has its own backend pipeline that actually scrapes the URL
        // / LinkedIn page. Use the dedicated endpoint instead of the unified
        // job queue so the URLs are honored.
        const resp = await apiPostJson<{ success?: boolean; jobId?: string; status?: string; error?: string; }>(
          '/workflows/research/execute',
          {
            url: researchUrl.trim() || '',
            linkedinUrl: researchLinkedIn.trim() || '',
            additionalText: researchNotes.trim() || context.trim() || '',
            documents: docIds.map((id) => ({ id })),
            clientId: project.customerId,
            projectId: project.id,
          }
        );
        if (!resp?.jobId) {
          throw new Error(resp?.error || 'Research server did not return a job id');
        }
        jobId = resp.jobId;
      } else {
        const req: any = {
          workflowType: chosen.type,
          customerId: project.customerId,
          projectId: project.id,
          documents: docIds,
          context: context.trim() || undefined,
        };
        if (isCoverage) {
          req.analysisMode = coverageMode;
          if (scenarioText.trim()) req.scenarioText = scenarioText.trim();
        }
        const resp = await jobsService.submitJob(token, req);
        if (!resp?.success || !resp.jobId) {
          throw new Error(resp?.message || 'Server did not return a job id');
        }
        jobId = resp.jobId;
      }

      addBackgroundReport({
        workflowId: jobId,
        workflowType: chosen.type,
        customerName,
        customerId: project.customerId,
        projectId: project.id,
        documents: documents
          .filter((d) => selectedDocIds.has(d.id))
          .map((d) => ({ id: d.id, name: d.name })),
      });

      // Pull the latest jobs immediately so the indicator updates without
      // waiting on the next 5s poll tick.
      refreshReports().catch(() => undefined);

      showToast({
        type: 'success',
        title: 'Report queued',
        message: `${chosen.label} for ${customerName} is running. You'll see it on the Reports tab when it's ready.`,
        duration: 6000,
      });

      navigate('/m/reports', { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to submit report');
      setSubmitting(false);
    }
  };

  return (
    <MobileLayout title="Run Report" back hideNav>
      <div className="px-3 pt-3">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Report type</h2>
        <div className="mt-2 space-y-2">
          {WORKFLOWS.map((w) => (
            <label
              key={w.type}
              className={`flex cursor-pointer items-start gap-3 rounded-xl bg-white p-3 ring-1 transition-colors dark:bg-slate-900 ${
                chosenType === w.type ? 'ring-2' : 'ring-slate-200 dark:ring-slate-700'
              }`}
              style={chosenType === w.type ? { boxShadow: `0 0 0 2px ${PRIMARY}` } : undefined}
            >
              <input
                type="radio"
                name="workflow"
                value={w.type}
                checked={chosenType === w.type}
                onChange={() => setChosenType(w.type)}
                className="mt-1 h-4 w-4"
                style={{ accentColor: PRIMARY }}
              />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-slate-900 dark:text-white">{w.label}</span>
                <span className="block text-xs text-slate-500 dark:text-slate-400">{w.description}</span>
                <span className="mt-0.5 block text-[11px] text-slate-400">
                  {w.minDocs === 0 ? 'No documents required' : `Needs ${w.minDocs}+ document${w.minDocs === 1 ? '' : 's'}`}
                </span>
              </span>
            </label>
          ))}
        </div>
      </div>

      {isResearch && (
        <div className="mt-4 space-y-3 px-3">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Research target
          </h2>
          <div className="rounded-xl bg-white p-3 shadow-sm ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-700">
            <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Company website URL
            </label>
            <input
              type="url"
              inputMode="url"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={researchUrl}
              onChange={(e) => setResearchUrl(e.target.value)}
              placeholder="https://example.com"
              className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
            />

            <label className="mt-3 block text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              LinkedIn URL <span className="normal-case text-slate-400">(optional)</span>
            </label>
            <input
              type="url"
              inputMode="url"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={researchLinkedIn}
              onChange={(e) => setResearchLinkedIn(e.target.value)}
              placeholder="https://linkedin.com/company/…"
              className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
            />

            <label className="mt-3 block text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Notes / specific questions <span className="normal-case text-slate-400">(optional)</span>
            </label>
            <textarea
              value={researchNotes}
              onChange={(e) => setResearchNotes(e.target.value)}
              rows={3}
              placeholder="e.g. 'They acquired a logistics arm last year — what's the new risk profile?'"
              className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
            />
            <p className="mt-2 text-[11px] text-slate-400">
              Provide at least one of: company URL or LinkedIn URL.
            </p>
          </div>
        </div>
      )}

      {isCoverage && (
        <div className="mt-4 px-3">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Coverage Analysis mode
          </h2>
          <div className="mt-2 space-y-2">
            {COVERAGE_MODES.map((m) => (
              <label
                key={m.id}
                className={`flex cursor-pointer items-start gap-3 rounded-xl bg-white p-3 ring-1 transition-colors dark:bg-slate-900 ${
                  coverageMode === m.id ? 'ring-2' : 'ring-slate-200 dark:ring-slate-700'
                }`}
                style={coverageMode === m.id ? { boxShadow: `0 0 0 2px ${PRIMARY}` } : undefined}
              >
                <input
                  type="radio"
                  name="coverage-mode"
                  value={m.id}
                  checked={coverageMode === m.id}
                  onChange={() => setCoverageMode(m.id)}
                  className="mt-1 h-4 w-4"
                  style={{ accentColor: PRIMARY }}
                />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-slate-900 dark:text-white">{m.label}</span>
                  <span className="block text-xs text-slate-500 dark:text-slate-400">{m.description}</span>
                </span>
              </label>
            ))}
          </div>

          {isScenarioMode && (
            <div className="mt-3">
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Scenario <span className="text-red-500 normal-case">*</span>
              </label>
              <textarea
                value={scenarioText}
                onChange={(e) => setScenarioText(e.target.value)}
                rows={4}
                placeholder="Describe the loss scenario you want evaluated against the policy (e.g. 'Roof collapsed under snow on 1/14; insured had ice-damage rider...')."
                className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
              />
            </div>
          )}
        </div>
      )}

      <div className="mt-4 px-3">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Documents ({selectedDocIds.size} selected)
        </h2>
      </div>
      <ul className="mt-2 divide-y divide-slate-100 bg-white dark:divide-slate-800 dark:bg-slate-900">
        {documents.map((d) => {
          const checked = selectedDocIds.has(d.id);
          return (
            <li key={d.id}>
              <label className="flex cursor-pointer items-center gap-3 px-3 py-3 active:bg-slate-50 dark:active:bg-slate-800">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(d.id)}
                  className="h-5 w-5"
                  style={{ accentColor: PRIMARY }}
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-slate-900 dark:text-white">{d.name}</span>
                  <span className="block text-[11px] text-slate-400">{(d.size / 1024).toFixed(0)} KB · {d.status || 'ready'}</span>
                </span>
              </label>
            </li>
          );
        })}
        {documents.length === 0 && (
          <li className="px-3 py-6 text-center text-sm text-slate-500">No documents in this project yet.</li>
        )}
      </ul>

      <div className="mt-4 px-3">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Additional context (optional)
        </h2>
        <textarea
          value={context}
          onChange={(e) => setContext(e.target.value)}
          rows={3}
          placeholder="Anything else the model should know? Time periods, specific questions, etc."
          className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
        />
      </div>

      {error && (
        <div className="mx-3 mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/30 dark:text-red-200">
          {error}
        </div>
      )}

      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white px-3 py-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] dark:border-slate-700 dark:bg-slate-900">
        {submitting ? (
          <div className="flex flex-col items-center gap-1">
            <div className="text-sm font-medium text-slate-700 dark:text-slate-200">Submitting…</div>
          </div>
        ) : (
          <button
            type="button"
            disabled={!canRun}
            onClick={run}
            className="w-full rounded-xl px-4 py-3 text-sm font-semibold text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-50"
            style={{ backgroundColor: PRIMARY }}
          >
            {!docsOk
              ? `Select ${chosen.minDocs} document${chosen.minDocs === 1 ? '' : 's'} to continue`
              : !researchOk
              ? 'Enter a company URL or LinkedIn URL to continue'
              : !scenarioOk
              ? 'Describe the scenario to continue'
              : `Queue ${chosen.label}`}
          </button>
        )}
      </div>
    </MobileLayout>
  );
};

export default MobileRunWorkflow;
